/**
 * `/internal/ops/revenue/*` contract types.
 *
 * Read-only revenue dashboard surface on platform-api. Epic #84 P11
 * gives ops visibility into the L1 (topup exchange-rate premium) +
 * L2 (token sales gross margin) layers populated in P3/P7.
 *
 * Ownership: platform-api owns `revenue_ledger` (migration 0014).
 * Every route here runs live `GROUP BY` + `SUM` aggregations — no
 * materialised view yet (P13 may add one if scale demands it). Query
 * paths use `idx_revenue_ledger_type_time` for time-filtered global
 * aggregates + `idx_revenue_ledger_account_time` for per-tenant
 * grouping.
 *
 * Caller: ops-api `/v1/ops/revenue/*` (role-gated `ops-admin`), which
 * proxies to these handlers with the shared-secret bearer.
 *
 * Money encoding: usage revenue carries micro-USD authoritative fields plus
 * integer-cents compatibility projections. The backend never divides for
 * display (avoids floating-point drift); the ops-console renders USD/CNY
 * divisions purely for display. Percentages are computed server-side with
 * zero-denominator -> 0 (never NaN on the wire).
 *
 * Time zone (epic #997 / #1001): business windows and day buckets use
 * the unified **UTC+8** business timezone (`BUSINESS_TIMEZONE` in
 * `internal-ops-business.ts`, the PnL convention from
 * `internal-ops-finance.ts`). `window=today` starts at the current
 * UTC+8 wall-clock midnight, and the `daily` endpoint's `date` strings
 * are UTC+8 business-day buckets.
 *
 * Window bounds (#1001): all business aggregates evaluate
 * `[from, to)` half-open — consistent with the gateway usage
 * aggregate, router-stats, and the #999 funnel COUNT windows — so a
 * row landing exactly on a window boundary is counted in exactly one
 * of two adjacent windows (previously `<= to` double-counted it).
 *
 * System-account exclusion (epic #997 / #998): the business aggregates
 * (`/summary`, `/daily`, `/by-model`, `/by-tenant`) exclude the
 * pre-seeded `acc_system` account by default — its ledger rows fund
 * probes/canaries (Architecture Rule 6 / ADR-003) and are
 * self-consumption, not business revenue. Callers that need full
 * ledger parity for reconciliation pass `includeSystem=true` (only the
 * literals `true` / `false` are accepted). An explicit
 * `accountId=acc_system` drill-down also bypasses the exclusion, and
 * `/balance-sources/health` (a reconciliation check, not a business
 * aggregate) always sees every account. Response shapes are unchanged
 * by the filter.
 */

// ─── Summary (today / 7d / 30d / custom) ───────────────────────────────

/**
 * One of the four predefined windows accepted by the summary endpoint.
 * `today` spans the current UTC+8 wall-clock day (see the module
 * header's timezone note). `custom` requires both `from` and `to`
 * query params; anything else ignores them.
 *
 * The 4-bucket enum keeps the server handler's window-to-SQL mapping
 * compact. Ops-console exposes the three fixed options as tabs;
 * `custom` is reserved for future ad-hoc reporting (no UI today).
 */
export type RevenueWindow = 'today' | '7d' | '30d' | 'custom';

/**
 * Headline topup-side numbers over the requested window.
 *
 * `cny_gross_cents` is the sum of what customers paid (pre-channel-fee).
 * `cny_channel_fee_cents` is the payment-provider fee (cost, not
 * premium). `cny_net_cents = gross - channel_fee` is what the operator
 * actually keeps at the CNY layer. `usd_credited_cents` is the
 * corresponding USD balance credited. `l1_profit_cny_cents` is the
 * exchange-rate premium realised at topup time.
 *
 * `l1_margin_pct` = `l1_profit / cny_gross`, expressed as a fraction
 * in `[0, 1]` (not a percent string). Display layer multiplies by 100.
 * Zero-denominator window → 0.
 */
export interface RevenueSummaryTopup {
  readonly count: number;
  readonly cny_gross_cents: number;
  readonly cny_net_cents: number;
  readonly cny_channel_fee_cents: number;
  readonly usd_credited_cents: number;
  readonly l1_profit_cny_cents: number;
  readonly l1_margin_pct: number;
}

/**
 * Headline usage-side numbers over the requested window.
 *
 * `usd_charged_micros` is the sum of what tenants were charged (balance
 * debits, USD layer); `usd_charged_cents` is the compatibility projection.
 * `cny_upstream_cost_cents` is the FIFO-allocated upstream cost (CNY layer).
 * `l2_profit_cny_cents` is the gross margin realised at consumption
 * time; can be negative when upstream cost exceeded revenue (stale
 * pricing, misidentified upstream).
 *
 * The `*_funded_usd_cents` fields split usage debits by the balance
 * source lots they consumed. `cash_funded_usd_cents` is paid top-up
 * balance, `gift_card_funded_usd_cents` is gift-card / gift-direct /
 * promoter_grant promotional credit, `rebate_funded_usd_cents` is
 * settled `promoter_rebate` (never `other_credit`),
 * `credit_draw_funded_usd_cents` is receivable overdraft spend
 * (`credit_draw`, never `other_credit`), and
 * `other_credit_funded_usd_cents` covers positive ops/bootstrap/
 * refund/legacy credit lots. `unallocated_usd_cents` is the part of
 * usage still missing a source allocation. Most of it is live credit /
 * negative-balance consumption whose charge wrote no lot allocation
 * because the allocator had nothing left to assign; a smaller remainder
 * is historical data from before the source-lot ledger was backfilled.
 * Per-day, per-account, per-brand detail is
 * `GET /internal/ops/revenue/unallocated-daily`.
 *
 * `l2_margin_pct` = `l2_profit / (usd_charged * market_rate_at_row)`.
 * Because market rate varies per row, the server computes the
 * denominator per-row inline (`SUM(usd_charged_micros * market_rate / 10000)`)
 * rather than snapshotting a single rate. Zero-denominator window → 0.
 */
export interface RevenueSummaryUsage {
  readonly count: number;
  /** Usage revenue charged to tenants in micro-USD. Source of truth. */
  readonly usd_charged_micros: number;
  /** Usage revenue charged to tenants in USD cents, compatibility projection. */
  readonly usd_charged_cents: number;
  readonly cash_funded_usd_micros?: number;
  readonly cash_funded_usd_cents: number;
  readonly gift_card_funded_usd_micros?: number;
  readonly gift_card_funded_usd_cents: number;
  readonly other_credit_funded_usd_micros?: number;
  readonly other_credit_funded_usd_cents: number;
  /**
   * Usage funded by settled `promoter_rebate` lots (epic #2456 / #2457).
   * Additive — older clients ignore it. Rebate never lands in
   * `other_credit_funded_*`.
   */
  readonly rebate_funded_usd_micros?: number;
  readonly rebate_funded_usd_cents?: number;
  /**
   * Usage funded by `credit_draw` lots (router#2472). Additive — older
   * clients ignore it. Receivable spend never lands in `other_credit_funded_*`.
   */
  readonly credit_draw_funded_usd_micros?: number;
  readonly credit_draw_funded_usd_cents?: number;
  readonly unallocated_usd_micros?: number;
  readonly unallocated_usd_cents: number;
  readonly cny_upstream_cost_cents: number;
  readonly l2_profit_cny_cents: number;
  readonly l2_margin_pct: number;
  /**
   * Set only when `l2_margin_pct`'s denominator is not the live
   * `SUM(usd_charged_micros * market_rate / 10000)`.
   * `hourly_rounded_cny_charged_cents`: served hours contributed
   * `cny_charged_cents_rr` (each row's product rounded, then summed)
   * instead of that float.
   */
  readonly l2_margin_denominator?: 'hourly_rounded_cny_charged_cents';
}

/**
 * `GET /internal/ops/revenue/summary` response.
 *
 * `window.from` / `window.to` echo the server-resolved ISO timestamps.
 * For named windows (`today`/`7d`/`30d`), `to` is the handler's
 * `now()` snapshot and `from` is derived; for `custom`, they're the
 * caller's values (trusted to be ISO-8601).
 *
 * `total_gross_margin_cny_cents` = `topup.l1_profit + usage.l2_profit`
 * computed server-side; saves the client from mixing CNY fields across
 * two objects.
 */
export interface RevenueSummaryResponse {
  readonly window: {
    readonly from: string;
    readonly to: string;
  };
  readonly topup: RevenueSummaryTopup;
  readonly usage: RevenueSummaryUsage;
  readonly total_gross_margin_cny_cents: number;
}

/**
 * One brand row from `GET /internal/ops/revenue/summary/by-brand`.
 *
 * Same finance fields as `RevenueSummaryResponse` plus `brand`. For any
 * window+filters, `brands[x]` matches `GET /summary?...&brand=x` on
 * every integer money field; `l2_margin_pct` (and the float8
 * `usage_revenue_cny_scaled` denominator it is derived from) may
 * differ by relative error `< 1e-9` because a single `GROUP BY brand`
 * pass changes float8 summation order.
 */
export interface RevenueSummaryByBrandRow {
  readonly brand: string;
  readonly topup: RevenueSummaryTopup;
  readonly usage: RevenueSummaryUsage;
  readonly total_gross_margin_cny_cents: number;
}

/**
 * `GET /internal/ops/revenue/summary/by-brand` response. One row per
 * brand from a single `GROUP BY brand` pass over the same predicate as
 * `/summary`, including the funding-attribution CTE.
 */
export interface RevenueSummaryByBrandResponse {
  readonly window: {
    readonly from: string;
    readonly to: string;
  };
  readonly brands: readonly RevenueSummaryByBrandRow[];
}

// ─── Balance-source ledger health ─────────────────────────────────────

/**
 * One account-level mismatch row from
 * `GET /internal/ops/revenue/balance-sources/health`.
 *
 * `*_usd_micros` values are authoritative. `*_usd_cents` values are
 * compatibility projections for older readers.
 */
export interface BalanceSourceHealthMismatch {
  readonly account_id: string;
  readonly source_remaining_usd_micros?: number;
  readonly source_remaining_usd_cents: number;
  readonly account_balance_usd_micros?: number;
  readonly account_balance_usd_cents: number;
  readonly signed_ledger_sum_usd_micros?: number;
  readonly signed_ledger_sum_usd_cents: number;
}

/**
 * Read-only reconciliation snapshot for the balance-source ledger.
 *
 * `is_balanced` is true only when:
 *   - every positive balance transaction has a source lot,
 *   - every negative debit (excluding a credit customer's within-line
 *     overdraw) has at least one source allocation, except a
 *     `gift_expiry_reclaim`, which instead must have zeroed its own lot
 *     (on any account, credit or not; `unallocated_negative_debits` counts
 *     the ones that did not),
 *   - global source remaining, net of the credit-funded shortfall, ==
 *     account balances == signed ledger sum at micro-USD precision,
 *   - no non-credit-explained account-level micro-USD mismatch exists.
 *
 * `credit_funded_shortfall_usd_micros` (epic #1033 E4-C3, #1127) is the
 * aggregate amount credit customers have legitimately consumed beyond their
 * held prepaid lots, each capped at its own `credit_limit_usd_micros`. It is
 * expected, not an alarm; it is netted out of the global equality above.
 *
 * `source_drift_positive_usd_micros` and `unallocated_usage_usd_micros`
 * (#2472) are observations only — they never participate in `is_balanced`.
 * The former is Σ GREATEST(lot remaining − account balance, 0) over every
 * account (no credit-limit clamp). The latter is the all-time per-charge
 * unallocated usage clamp, same SQL as `/unallocated-daily`. Tokyo
 * production, one `BEGIN TRANSACTION ISOLATION LEVEL REPEATABLE READ
 * READ ONLY` snapshot on 2026-09-16: source_drift_positive =
 * 8,894,469,463 micros, unallocated_usage = 8,894,480,601 micros,
 * difference = 11,138 micros ≈ $0.0111. That manual measurement used
 * one repeatable-read snapshot on purpose: reading the two sides at
 * different wall-clock moments inflates the gap, because both move
 * together as credit is drawn. This handler, by contrast, issues them
 * as two separate queries (the unallocated scan inside the
 * `SET LOCAL work_mem` transaction, the drift scan afterwards on the
 * pool connection), so the two fields in one response are not a
 * consistent snapshot of each other and may diverge by more than the
 * figure above on a busy system. Neither field is a predicate — they
 * never feed `is_balanced` — so a divergence is an observation, not
 * an alarm.
 *
 * `generated_at` is when this snapshot was computed. platform-api may serve
 * a process-local copy for up to five minutes (#2263). A live recompute is
 * `?refresh=1` (ops-api forwards that query key). `Cache-Control` is not a
 * bypass: ops-api does not forward request headers, and the handler does
 * not read them.
 */
export interface BalanceSourceHealthResponse {
  readonly generated_at: string;
  readonly is_balanced: boolean;
  readonly missing_positive_lots: number;
  readonly unallocated_negative_debits: number;
  readonly credit_funded_shortfall_usd_micros?: number;
  readonly credit_funded_shortfall_usd_cents?: number;
  readonly source_drift_positive_usd_micros?: number;
  readonly unallocated_usage_usd_micros?: number;
  readonly source_remaining_usd_micros?: number;
  readonly source_remaining_usd_cents: number;
  readonly account_balance_usd_micros?: number;
  readonly account_balance_usd_cents: number;
  readonly signed_ledger_sum_usd_micros?: number;
  readonly signed_ledger_sum_usd_cents: number;
  readonly mismatch_account_count: number;
  readonly sample_mismatches: readonly BalanceSourceHealthMismatch[];
}

/**
 * `GET /internal/ops/revenue/balance-sources/health/last` (#2685): the last
 * {@link BalanceSourceHealthResponse} any request computed, fleet-wide,
 * whatever its age — its own `generated_at` says when. `null` when nothing
 * has been computed within the shared copy's retention. This read never
 * runs the reconciliation scan; `/balance-sources/health` is the only
 * computing read.
 */
export interface BalanceSourceHealthLastResponse {
  readonly snapshot: BalanceSourceHealthResponse | null;
}

// ─── Daily trend ────────────────────────────────────────────────────────

/**
 * One per-day row from `GET /internal/ops/revenue/daily`. `date` is a
 * `YYYY-MM-DD` **UTC+8** wall-clock day (see module header note on the
 * unified business timezone, #1001). L1/L2 values are integer
 * CNY-cents summed over the day.
 *
 * A day with zero rows of either type reports 0 (not null) — the
 * handler synthesises a zero row for every day in the requested range
 * so the frontend chart can render a continuous x-axis without
 * client-side gap-filling.
 */
export interface RevenueDailyDay {
  readonly date: string;
  /** Top-up gross revenue in CNY cents for the day. Older callers may omit it. */
  readonly topup_cny_gross_cents?: number;
	  /** Usage revenue charged to tenants in USD cents for the day. Older callers may omit it. */
	  readonly usage_usd_charged_cents?: number;
	  /** Usage revenue charged to tenants in micro-USD for the day. Older callers may omit it. */
	  readonly usage_usd_charged_micros?: number;
  readonly l1_profit_cny_cents: number;
  readonly l2_profit_cny_cents: number;
  readonly topup_count: number;
  readonly usage_count: number;
}

export interface RevenueDailyResponse {
  readonly days: readonly RevenueDailyDay[];
}

/**
 * One (date, account, brand) row from
 * `GET /internal/ops/revenue/unallocated-daily`. `date` is a `YYYY-MM-DD`
 * UTC+8 business day. `brand` is `''` when the ledger row has no brand.
 *
 * `usage_count` is the number of usage charges on that day for that
 * account × brand whose per-charge clamp is strictly positive — not the
 * total usage-charge count. `unallocated_usd_*` is that clamp, matching
 * `/summary`'s unallocated field (per-tx charged minus allocated).
 */
export interface RevenueUnallocatedDailyRow {
  readonly date: string;
  readonly account_id: string;
  readonly brand: string;
  readonly usage_count: number;
  readonly unallocated_usd_micros: number;
  readonly unallocated_usd_cents: number;
}

/**
 * `GET /internal/ops/revenue/unallocated-daily?days=N` response.
 *
 * `from` / `to` are inclusive UTC+8 business-day bounds of the requested
 * window. Unlike `/summary`, this scan does not exclude `acc_system`:
 * credit-draw visibility is fleet-wide. Callers that need a subset filter
 * the `days` array themselves.
 */
export interface RevenueUnallocatedDailyResponse {
  readonly generated_at: string;
  readonly from: string;
  readonly to: string;
  readonly days: readonly RevenueUnallocatedDailyRow[];
}

// ─── By-model L2 breakdown ──────────────────────────────────────────────

/**
 * Per-model aggregate from `GET /internal/ops/revenue/by-model`. Rows
 * missing `model_vendor` / `model_name` (legacy data) are grouped
 * under the string `'(unknown)'` — the handler never returns NULL
 * here, so the frontend can render without null-handling branches.
 *
 * Ordering: by `l2_profit_cny_cents DESC` — biggest contributors
 * first. Tied rows sorted by `usage_count DESC` then vendor/name.
 */
export interface RevenueByModelRow {
	  readonly model_vendor: string;
	  readonly model_name: string;
	  readonly usage_count: number;
	  readonly usd_charged_micros: number;
	  readonly usd_charged_cents: number;
  /** USD charged converted per ledger row using the row's market_rate snapshot. */
  readonly cny_charged_cents: number;
  readonly cny_upstream_cost_cents: number;
  /** CNY upstream cost (micros-first dual read) ÷ each row's market_rate snapshot, summed, then rounded to cents once (router#2591). */
  readonly usd_upstream_cost_cents: number;
  readonly l2_profit_cny_cents: number;
  /** CNY L2 profit converted per ledger row using the row's market_rate snapshot. */
  readonly l2_profit_usd_cents: number;
  readonly l2_margin_pct: number;
  /** See {@link RevenueSummaryUsage.l2_margin_denominator}. */
  readonly l2_margin_denominator?: 'hourly_rounded_cny_charged_cents';
}

export interface RevenueByModelResponse {
  readonly models: readonly RevenueByModelRow[];
}

// ─── By-model daily trend (epic #1643, #1645) ───────────────────────────

/**
 * One per-day point in a `RevenueByModelDailySeries`. `date` is a
 * `YYYY-MM-DD` **UTC+8** wall-clock business day (module header note,
 * #1001), same bucketing as `/daily`.
 *
 * `usd_charged_micros` is authoritative; `usd_charged_cents` is the SUM
 * of the stored cents column — never derived from micros, so the two
 * fields stay independently reconcilable against the ledger.
 *
 * `cost_missing_count` counts rows whose `cny_upstream_cost_cents` is
 * NULL (unknown cost ≠ 0 cost, ADR-007): those rows contribute nothing
 * to `cny_upstream_cost_cents` / `l2_profit_cny_cents`, and this count
 * lets the frontend flag days whose margin is understated rather than
 * silently rendering the partial sum as truth.
 */
export interface RevenueByModelDailyPoint {
  readonly date: string;
  readonly usage_count: number;
  readonly usd_charged_micros: number;
  readonly usd_charged_cents: number;
  readonly cny_upstream_cost_cents: number;
  readonly l2_profit_cny_cents: number;
  readonly cost_missing_count: number;
}

/**
 * One model's day-by-day usage revenue series from
 * `GET /internal/ops/revenue/by-model/daily`. Vendor/name follow the
 * `/by-model` normalisation — legacy rows missing either field are
 * grouped under `'(unknown)'`, never NULL on the wire.
 *
 * `days` is zero-filled: exactly one point per business day in the
 * requested window, ascending, so the chart x-axis is continuous
 * without client-side gap-filling (same synthesis as `/daily`).
 */
export interface RevenueByModelDailySeries {
  readonly model_vendor: string;
  readonly model_name: string;
  readonly total_usage_count: number;
  readonly total_usd_charged_micros: number;
  readonly total_usd_charged_cents: number;
  readonly days: readonly RevenueByModelDailyPoint[];
}

/**
 * `GET /internal/ops/revenue/by-model/daily` response.
 *
 * `series` holds the top-N model groups ranked by
 * `total_usd_charged_micros DESC` (the authoritative revenue lens);
 * `model_count` is the distinct group count BEFORE truncation so the
 * frontend can render "showing N of M models" honestly.
 */
export interface RevenueByModelDailyResponse {
  readonly window: {
    readonly from: string;
    readonly to: string;
  };
  readonly model_count: number;
  readonly series: readonly RevenueByModelDailySeries[];
}

// ─── By-SKU unit economics (epic #997 P1.4, #1006) ──────────────────────

/**
 * Per-SKU aggregate from `GET /internal/ops/revenue/by-sku`. Grouped by
 * `revenue_ledger.sku_id` (usage rows only) — the SKU attribution
 * stamped by the gateway since #1000 (migration 0068).
 *
 * Row identity:
 *   - `sku_id` non-null: a marketplace SKU (`channel_models.id`).
 *     `sku_name` is the canonical `namespace/model` display name
 *     (`channels.slug || '/' || channel_models.model`), or the literal
 *     `'(unknown)'` when the catalog row was deleted after the ledger
 *     rows were written (`sku_id` is a soft ref — no FK).
 *   - `sku_id` null: the **legacy-path bucket** — usage charged through
 *     the pre-#904 channel-based path (including all rows written
 *     before 2026-07-07). Reported as an explicit row, never dropped;
 *     `sku_name` is null and the UI labels it. It shrinks as SKU-mode
 *     adoption grows.
 *
 * Catalog completeness: every current `channel_models` SKU appears even
 * with zero ledger activity in the window (all-zero economics), so
 * "priced but earning nothing" is visible and coverage joins by name
 * always resolve a `sku_id`.
 *
 * Money fields mirror `RevenueByModelRow` (same per-row `market_rate`
 * conversions). Ordering: `l2_profit_cny_cents DESC`, then
 * `usage_count DESC`, then name/id for deterministic ties.
 */
export interface RevenueBySkuRow {
  readonly sku_id: string | null;
  readonly sku_name: string | null;
  readonly usage_count: number;
  readonly usd_charged_micros: number;
  readonly usd_charged_cents: number;
  /** USD charged converted per ledger row using the row's market_rate snapshot. */
  readonly cny_charged_cents: number;
  readonly cny_upstream_cost_cents: number;
  /** CNY upstream cost (micros-first dual read) ÷ each row's market_rate snapshot, summed, then rounded to cents once (router#2591). */
  readonly usd_upstream_cost_cents: number;
  readonly l2_profit_cny_cents: number;
  /** CNY L2 profit converted per ledger row using the row's market_rate snapshot. */
  readonly l2_profit_usd_cents: number;
  readonly l2_margin_pct: number;
  /** See {@link RevenueSummaryUsage.l2_margin_denominator}. */
  readonly l2_margin_denominator?: 'hourly_rounded_cny_charged_cents';
}

/**
 * Whole-window sums across every ledger group in `skus` — including the
 * legacy bucket and `'(unknown)'` rows. Computed from the same grouped
 * scan with the same filters as `/summary`, so for any window+brand the
 * totals reconcile exactly with `RevenueSummaryResponse.usage`
 * (#1006 AC 1; asserted by a shared fixture test).
 */
export interface RevenueBySkuTotals {
  readonly usage_count: number;
  readonly usd_charged_micros: number;
  readonly usd_charged_cents: number;
  readonly cny_charged_cents: number;
  readonly cny_upstream_cost_cents: number;
  readonly usd_upstream_cost_cents: number;
  readonly l2_profit_cny_cents: number;
  readonly l2_profit_usd_cents: number;
  readonly l2_margin_pct: number;
  /** See {@link RevenueSummaryUsage.l2_margin_denominator}. */
  readonly l2_margin_denominator?: 'hourly_rounded_cny_charged_cents';
}

/**
 * `GET /internal/ops/revenue/by-sku` response. Same query-param surface
 * as `/by-model` (`from`/`to`/`brand`/`accountId`/`includeSystem`,
 * half-open `[from, to)` window, `acc_system` excluded by default).
 */
export interface RevenueBySkuResponse {
  readonly window: {
    readonly from: string;
    readonly to: string;
  };
  readonly totals: RevenueBySkuTotals;
  readonly skus: readonly RevenueBySkuRow[];
}

// ─── Group series (per-SKU / per-upstream trends, #1675) ────────────────

/**
 * Bucket granularity for the `/by-sku/series` + `/by-upstream/series`
 * endpoints. `day` buckets are UTC+8 business days (module header note,
 * #1001), labelled `YYYY-MM-DD`; `hour` buckets are UTC+8 wall-clock
 * hours labelled `YYYY-MM-DD HH:00`. Absent/empty defaults to `'day'`.
 */
export type RevenueSeriesGranularity = 'day' | 'hour';

/**
 * One time bucket in a revenue group series. `bucket` is the UTC+8
 * label per `RevenueSeriesGranularity`. Buckets cover every UTC+8
 * day/hour intersecting the half-open `[from, to)` window, zero-filled
 * and ascending, so the chart x-axis is continuous without client-side
 * gap-filling.
 *
 * Money rules match `RevenueByModelDailyPoint`: `usd_charged_micros` is
 * authoritative; `usd_charged_cents` is the SUM of the stored cents
 * column — never derived from micros; `cost_missing_count` counts rows
 * whose `cny_upstream_cost_cents` is NULL (unknown cost ≠ 0 cost,
 * ADR-007) that the cost/profit sums silently skipped.
 */
export interface RevenueSeriesPoint {
  readonly bucket: string;
  readonly usage_count: number;
  readonly usd_charged_micros: number;
  readonly usd_charged_cents: number;
  readonly cny_upstream_cost_cents: number;
  readonly l2_profit_cny_cents: number;
  readonly cost_missing_count: number;
}

/**
 * Shared per-group series shape. `label` is the raw group identifier
 * (`sku_id` / `upstream_id`) or the literal `'(unknown)'` for the
 * NULL-attribution group — deliberately NOT a display name: SKU display
 * names come from `/by-sku` (which the hub fetches alongside) and
 * upstream display names are gateway-owned, so this surface never
 * joins either. Totals aggregate the same fields as the points.
 */
export interface RevenueGroupSeries {
  readonly label: string;
  readonly total_usage_count: number;
  readonly total_usd_charged_micros: number;
  readonly total_usd_charged_cents: number;
  readonly total_cny_upstream_cost_cents: number;
  readonly total_l2_profit_cny_cents: number;
  readonly total_cost_missing_count: number;
  readonly points: readonly RevenueSeriesPoint[];
}

/**
 * One SKU's bucket-by-bucket usage series from
 * `GET /internal/ops/revenue/by-sku/series`. `sku_id` null is the
 * legacy-path bucket (rows charged before SKU attribution, #1000) —
 * all NULL rows merge into this single `'(unknown)'`-labelled group.
 */
export interface RevenueSkuSeries extends RevenueGroupSeries {
  readonly sku_id: string | null;
}

/**
 * One upstream channel's bucket-by-bucket usage series from
 * `GET /internal/ops/revenue/by-upstream/series`. `upstream_id` null
 * rows merge into one `'(unknown)'`-labelled group.
 */
export interface RevenueUpstreamSeries extends RevenueGroupSeries {
  readonly upstream_id: string | null;
}

/**
 * `GET /internal/ops/revenue/by-sku/series` /
 * `GET /internal/ops/revenue/by-upstream/series` response envelope.
 *
 * `series` contains EVERY group in the window (explicitly no top-N /
 * `limit` param — the metrics hub renders an independent trend per
 * group), ranked by `total_usd_charged_micros DESC` with code-point
 * identifier tie-breaks; `group_count` therefore always equals
 * `series.length` and exists so a future truncation would be an
 * explicit, visible contract change.
 *
 * Defaults (documented for the parameterless ops-readonly sweep): `to`
 * defaults to now, `from` to `to - REVENUE_SERIES_DEFAULT_DAYS` days,
 * `granularity` to `'day'` — so a bare GET returns a trailing-30-day
 * daily series. Range caps: `granularity=day` rejects spans over
 * `REVENUE_SERIES_DAY_MAX_DAYS` days; `granularity=hour` rejects spans
 * over `REVENUE_SERIES_HOUR_MAX_HOURS` hours (both 400).
 */
export interface RevenueBySkuSeriesResponse {
  readonly window: {
    readonly from: string;
    readonly to: string;
  };
  readonly granularity: RevenueSeriesGranularity;
  readonly group_count: number;
  readonly series: readonly RevenueSkuSeries[];
}

export interface RevenueByUpstreamSeriesResponse {
  readonly window: {
    readonly from: string;
    readonly to: string;
  };
  readonly granularity: RevenueSeriesGranularity;
  readonly group_count: number;
  readonly series: readonly RevenueUpstreamSeries[];
}

// ─── Throughput series (per-SKU / per-upstream RPM+TPM trends, #1698) ───

/**
 * Bucket granularity for the `/by-sku/throughput-series` +
 * `/by-upstream/throughput-series` endpoints. Extends the revenue
 * series granularities with `'minute'`: throughput is an operational
 * lens (RPM/TPM), so operators need sub-hour resolution the money
 * trends deliberately don't offer. `day`/`hour` buckets follow the
 * same UTC+8 conventions and labels as `RevenueSeriesGranularity`;
 * `minute` buckets are UTC+8 wall-clock minutes labelled
 * `YYYY-MM-DD HH:MM`. Absent/empty defaults to `'day'`.
 */
export type ThroughputSeriesGranularity = RevenueSeriesGranularity | 'minute';

/**
 * One time bucket in a throughput group series. `bucket` is the UTC+8
 * label per `ThroughputSeriesGranularity`. Buckets cover every UTC+8
 * day/hour/minute intersecting the half-open `[from, to)` window,
 * zero-filled and ascending — same continuous-axis contract as
 * `RevenueSeriesPoint`.
 *
 * Traffic rules (deliberately NOT the money rules): `request_count`
 * counts EVERY `usage_rated_events` row regardless of
 * `rating_outcome` — a zero-cost or unpriced request still consumed
 * upstream capacity, and throughput measures traffic, not revenue.
 * Failed requests (`status_code >= 400`) are likewise counted: a
 * failure response consumed upstream capacity too. Token fields are
 * plain SUMs of the per-request token columns.
 */
export interface ThroughputSeriesPoint {
  readonly bucket: string;
  readonly request_count: number;
  readonly input_tokens: number;
  readonly output_tokens: number;
  readonly cache_read_tokens: number;
  readonly cache_write_tokens: number;
}

/**
 * Shared per-group throughput series shape. `label` is the raw group
 * identifier (`sku_id` / `upstream_id`) or the literal `'(unknown)'`
 * for the NULL-attribution group — same no-display-name-join decision
 * as `RevenueGroupSeries`. Totals aggregate the same fields as the
 * points.
 */
export interface ThroughputGroupSeries {
  readonly label: string;
  readonly total_request_count: number;
  readonly total_input_tokens: number;
  readonly total_output_tokens: number;
  readonly total_cache_read_tokens: number;
  readonly total_cache_write_tokens: number;
  readonly points: readonly ThroughputSeriesPoint[];
}

/**
 * One SKU's bucket-by-bucket throughput series from
 * `GET /internal/ops/revenue/by-sku/throughput-series`. `sku_id` null
 * rows merge into one `'(unknown)'`-labelled group.
 */
export interface ThroughputSkuSeries extends ThroughputGroupSeries {
  readonly sku_id: string | null;
}

/**
 * One upstream channel's bucket-by-bucket throughput series from
 * `GET /internal/ops/revenue/by-upstream/throughput-series`.
 * `upstream_id` null rows merge into one `'(unknown)'`-labelled group.
 */
export interface ThroughputUpstreamSeries extends ThroughputGroupSeries {
  readonly upstream_id: string | null;
}

/**
 * `GET /internal/ops/revenue/by-sku/throughput-series` /
 * `GET /internal/ops/revenue/by-upstream/throughput-series` response
 * envelope. Same every-group / ranking / default-window contract as
 * the revenue series envelopes, except groups rank by
 * `total_request_count DESC` (the traffic lens) with code-point
 * identifier tie-breaks. Range caps: `day` ≤
 * `REVENUE_SERIES_DAY_MAX_DAYS` days, `hour` ≤
 * `REVENUE_SERIES_HOUR_MAX_HOURS` hours, `minute` ≤
 * `REVENUE_THROUGHPUT_MINUTE_MAX_MINUTES` minutes (all reject 400).
 * A maximal minute window therefore yields at most 361 one-minute
 * points per group (361 when the window is not minute-aligned) — the
 * span caps are payload/load bounds, not a top-N: every group is still
 * returned, and introducing group truncation would be an explicit
 * contract change.
 */
export interface ThroughputBySkuSeriesResponse {
  readonly window: {
    readonly from: string;
    readonly to: string;
  };
  readonly granularity: ThroughputSeriesGranularity;
  readonly group_count: number;
  readonly series: readonly ThroughputSkuSeries[];
}

export interface ThroughputByUpstreamSeriesResponse {
  readonly window: {
    readonly from: string;
    readonly to: string;
  };
  readonly granularity: ThroughputSeriesGranularity;
  readonly group_count: number;
  readonly series: readonly ThroughputUpstreamSeries[];
}

// ─── By-upstream window aggregate (#1675) ───────────────────────────────

/**
 * Per-upstream aggregate from `GET /internal/ops/revenue/by-upstream`.
 * Grouped by `revenue_ledger.upstream_id` (usage rows only). Money
 * fields mirror `RevenueBySkuRow` (same per-row `market_rate`
 * conversions), plus `cost_missing_count` — the count of rows whose
 * `cny_upstream_cost_cents` is NULL (ADR-007: unknown ≠ 0), which the
 * cost/profit sums silently skipped.
 *
 * `upstream_id` null is the unattributed bucket; those rows merge into
 * one row labelled `'(unknown)'`. `label` is otherwise the raw
 * `upstream_id` — upstream display names are gateway-owned and
 * deliberately not joined here. Ordering mirrors `/by-sku`:
 * `l2_profit_cny_cents DESC`, then `usage_count DESC`, then label
 * (code-point comparison).
 */
export interface RevenueByUpstreamRow {
  readonly upstream_id: string | null;
  readonly label: string;
  readonly usage_count: number;
  readonly usd_charged_micros: number;
  readonly usd_charged_cents: number;
  /** USD charged converted per ledger row using the row's market_rate snapshot. */
  readonly cny_charged_cents: number;
  readonly cny_upstream_cost_cents: number;
  /** CNY upstream cost (micros-first dual read) ÷ each row's market_rate snapshot, summed, then rounded to cents once (router#2591). */
  readonly usd_upstream_cost_cents: number;
  readonly l2_profit_cny_cents: number;
  /** CNY L2 profit converted per ledger row using the row's market_rate snapshot. */
  readonly l2_profit_usd_cents: number;
  readonly l2_margin_pct: number;
  /** See {@link RevenueSummaryUsage.l2_margin_denominator}. */
  readonly l2_margin_denominator?: 'hourly_rounded_cny_charged_cents';
  readonly cost_missing_count: number;
}

/** Whole-window sums across every group in `upstreams`. */
export interface RevenueByUpstreamTotals {
  readonly usage_count: number;
  readonly usd_charged_micros: number;
  readonly usd_charged_cents: number;
  readonly cny_charged_cents: number;
  readonly cny_upstream_cost_cents: number;
  readonly usd_upstream_cost_cents: number;
  readonly l2_profit_cny_cents: number;
  readonly l2_profit_usd_cents: number;
  readonly l2_margin_pct: number;
  /** See {@link RevenueSummaryUsage.l2_margin_denominator}. */
  readonly l2_margin_denominator?: 'hourly_rounded_cny_charged_cents';
  readonly cost_missing_count: number;
}

/**
 * `GET /internal/ops/revenue/by-upstream` response. Accepts either a
 * named `window=` (same literals as `/summary`) or a free-form
 * `from=`/`to=` pair (same defaults + span cap as `/by-sku`); half-open
 * `[from, to)`, `acc_system` excluded unless `includeSystem=true`.
 */
export interface RevenueByUpstreamResponse {
  readonly window: {
    readonly from: string;
    readonly to: string;
  };
  readonly totals: RevenueByUpstreamTotals;
  readonly upstreams: readonly RevenueByUpstreamRow[];
}

// ─── Top-tenants contribution ───────────────────────────────────────────

/**
 * Per-tenant lifetime aggregate from `GET /internal/ops/revenue/by-tenant`.
 * `total_cny_cents` = `l1_profit_cny_cents + l2_profit_cny_cents`.
 * Sorted per the `sort` param (default `'profit'` = `total_cny_cents
 * DESC`), default limit 50, max 200.
 *
 * `account_email` is joined from `accounts` — when the email is empty
 * string (should not happen in Phase 1) or the account row somehow
 * missing (shouldn't happen; revenue_ledger has a soft ref only), the
 * handler returns empty string rather than NULL.
 */
export interface RevenueByTenantRow {
  readonly account_id: string;
  readonly account_email: string;
	  readonly topup_count: number;
	  readonly l1_profit_cny_cents: number;
	  readonly usage_count: number;
	  readonly usage_usd_charged_micros: number;
	  readonly usage_usd_charged_cents: number;
  readonly usage_cny_upstream_cost_cents: number;
  readonly l2_profit_cny_cents: number;
  readonly total_cny_cents: number;
}

export interface RevenueByTenantResponse {
  readonly tenants: readonly RevenueByTenantRow[];
}

/**
 * Orderings accepted by the by-tenant endpoint's optional `sort` query
 * param. Applied in SQL BEFORE the `limit` truncation, so the returned
 * page is the true top-N under the requested lens:
 *
 *   - `'profit'` (default): `total_cny_cents DESC` — the admin
 *     contribution lens. Default preserved for existing callers.
 *   - `'spend'`: `usage_usd_charged_micros DESC` — the CM/sales
 *     consumption lens (#1065). Without this, a "top customers by
 *     spend" consumer re-ranking a profit-truncated page can silently
 *     lose a true top spender whose margin is low or negative.
 *
 * Ties break by `account_id ASC` under both orderings.
 */
export type RevenueByTenantSort = 'profit' | 'spend';

/** All accepted `sort` literals, for validation at the route boundary. */
export const REVENUE_BY_TENANT_SORTS: readonly RevenueByTenantSort[] = ['profit', 'spend'];

// ─── Shared defaults ────────────────────────────────────────────────────

/**
 * Default + max tenant rows returned by the by-tenant endpoint.
 * Matches the pagination defaults on `/internal/admin/accounts`
 * (ops-console tenant table) so operators see a consistent page size
 * across the two surfaces.
 */
export const REVENUE_BY_TENANT_DEFAULT_LIMIT = 50;
export const REVENUE_BY_TENANT_MAX_LIMIT = 200;

/**
 * Default day-count for the daily trend endpoint. Aligns with the
 * ops-console "30-day chart" default and the `router-stats` admin
 * dashboard's longest window.
 */
export const REVENUE_DAILY_DEFAULT_DAYS = 30;
export const REVENUE_DAILY_MAX_DAYS = 90;

/**
 * Default + max series count for the by-model daily endpoint (#1645).
 * Eight distinguishable lines is the practical ceiling for a stacked/
 * multi-line trend chart; unlike the by-tenant limit, a value above the
 * max rejects with 400 rather than clamping — the response would
 * silently misrepresent "top N" otherwise.
 */
export const REVENUE_BY_MODEL_DAILY_DEFAULT_LIMIT = 8;
export const REVENUE_BY_MODEL_DAILY_MAX_LIMIT = 20;

/**
 * Range limits for the group-series endpoints (#1675). Day-granularity
 * series cap the `from`/`to` span at 90 days — same payload bound as
 * `/by-model/daily`'s clamped `days` — and hour-granularity series cap
 * it at 48 hours (hour buckets are only offered for ranges of at most
 * one-to-two days). Both reject with 400 rather than clamping: a
 * silently narrowed window would misrepresent the chart the caller
 * asked for. A parameterless call defaults to a trailing
 * `REVENUE_SERIES_DEFAULT_DAYS`-day day-granularity window.
 */
export const REVENUE_SERIES_DAY_MAX_DAYS = 90;
export const REVENUE_SERIES_HOUR_MAX_HOURS = 48;
export const REVENUE_SERIES_DEFAULT_DAYS = 30;

/**
 * Range limit for minute-granularity throughput series (#1698). Six
 * hours of one-minute buckets (≤ 361 points — 361 when the window is
 * not minute-aligned) — the live-ops zoom window (a spike
 * investigation looks at the last few hours, not a week) — and the same
 * order of payload magnitude as the 48-hour hour cap. Rejects with 400
 * rather than clamping, matching the other series caps.
 */
export const REVENUE_THROUGHPUT_MINUTE_MAX_MINUTES = 360;

/**
 * Maximum span (days) accepted on endpoints that take a free-form
 * `from=/to=` pair (`/summary?window=custom`, `/by-model`,
 * `/by-tenant`). Without a cap an ops-admin can request
 * `from=1970-01-01&to=2099-12-31`, triggering a full-table scan
 * with no LIMIT and saturating SQL read units. 366 days covers a
 * realistic annual report without opening that hole. Server-enforced
 * — the ops-console doesn't expose a custom-range picker today, so
 * this is a defensive guard against direct `/internal/*` callers.
 */
export const REVENUE_CUSTOM_MAX_DAYS = 366;
